import React from 'react'
import PropTypes from 'prop-types'
import {get} from 'lodash-es'
import useSWR from 'swr'
import {
  Box,
  Text,
  Flex,
  Tooltip,
  Icon,
  Divider,
  useOutsideClick,
  Skeleton,
} from '@chakra-ui/react'
import {TriangleDownIcon, TriangleUpIcon} from '@chakra-ui/icons'
import {fetcher} from '../../../../../api'
import {camelizeResult, dineroMoneyObjects} from '../../../../../api/serializer'
import {useRails} from '../../../../contexts/rails'

const FeeBreakdown = ({
  id,
  feeAmount,
  feeRate,
  showBreakdown,
  priceWithinMaximumLimit,
  maximumFeeRate,
}) => {
  const ref = React.useRef()
  const [showDisplayBreakdown, setShowDisplayBreakdown] = React.useState(false)
  useOutsideClick({
    ref,
    handler: () => setShowDisplayBreakdown(false),
  })

  const {organisation} = useRails()

  const fixedPricingEnabled = get(
    organisation,
    'currentSupplier.fixedPricingEnabled',
    false,
  )
  const {data, isLoading} = useSWR(
    showDisplayBreakdown
      ? `/api/suppliers/${organisation.currentSupplier.id}/invoices/${id}/fee_breakdown`
      : null,
    fetcher,
    {
      use: [camelizeResult, dineroMoneyObjects],
    },
  )

  if (!priceWithinMaximumLimit) {
    return <Text textStyle="body-small">Fee: Over {maximumFeeRate}</Text>
  }

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      ref={ref}
      onClick={() => {
        setShowDisplayBreakdown(!showDisplayBreakdown)
      }}
    >
      <Text
        cursor={showBreakdown && !fixedPricingEnabled ? 'pointer' : ''}
        wrap="nowrap"
      >
        <Text
          as="span"
          textStyle="body-small"
          color="grey.700"
          fontWeight="medium"
        >
          Fee:
        </Text>{' '}
        <Text as="span" textStyle="body-small">
          {feeAmount}
        </Text>{' '}
        <Text
          as="span"
          textStyle="body-small"
          fontWeight="medium"
          display={{base: 'none', md: 'inline'}}
        >
          ({feeRate})
        </Text>
        {showBreakdown && !fixedPricingEnabled && (
          <Tooltip
            isOpen={showDisplayBreakdown}
            pointerEvents="all"
            label={
              <Box bg="neutral.white" textStyle="body-copy">
                <Text textStyle="headline5" textTransform="uppercase" mb="xs">
                  Fee breakdown:
                </Text>
                <Flex mb="xs" px="xs" direction="column" gap="xs">
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                  >
                    <Flex width="128px">
                      <Text>Status fee</Text>
                    </Flex>
                    <Skeleton isLoaded={!isLoading} minW="100px">
                      <Text textStyle="body-copy-medium">
                        {data && data.supplierFeeAmount.toFormat()}
                        <Text
                          as="span"
                          ml="xs"
                          color="grey.700"
                          textStyle="body-copy"
                        >
                          ({data && data.supplierFeeRate})
                        </Text>
                      </Text>
                    </Skeleton>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                  >
                    <Flex width="128px">
                      <Text>Customer fee</Text>
                    </Flex>
                    <Skeleton isLoaded={!isLoading} minW="100px">
                      <Text textStyle="body-copy-medium">
                        {data && data.customerFeeAmount.toFormat()}
                        <Text
                          as="span"
                          ml="xs"
                          color="grey.700"
                          textStyle="body-copy"
                        >
                          ({data && data.customerFeeRate})
                        </Text>
                      </Text>
                    </Skeleton>
                  </Box>

                  <Divider variant="dark" />

                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    textStyle="body-copy"
                  >
                    <Text>Cash-in fee</Text>
                    <Text textStyle="body-copy-medium">
                      {feeAmount}
                      <Text
                        as="span"
                        ml="xs"
                        color="grey.700"
                        textStyle="body-copy"
                      >
                        ({feeRate})
                      </Text>
                    </Text>
                  </Box>
                </Flex>
              </Box>
            }
            w="md"
            p="sm"
            boxShadow="base"
            borderRadius="md"
            bg="neutral.white"
            color="black"
          >
            <Icon
              as={showDisplayBreakdown ? TriangleUpIcon : TriangleDownIcon}
              color="mld.primary.100"
              cursor="pointer"
              ml="xxs"
              h="8px"
              w="8px"
              data-testid="tooltip-icon"
            />
          </Tooltip>
        )}
      </Text>
    </Box>
  )
}

FeeBreakdown.defaultProps = {
  showBreakdown: false,
}

FeeBreakdown.propTypes = {
  id: PropTypes.string.isRequired,
  feeAmount: PropTypes.string.isRequired,
  feeRate: PropTypes.string.isRequired,
  showBreakdown: PropTypes.bool,
  priceWithinMaximumLimit: PropTypes.bool.isRequired,
  maximumFeeRate: PropTypes.string.isRequired,
}

export default FeeBreakdown
